import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { BranchBadge } from './BranchSelector';
import { getStatusBadge, getPortalBadge } from './dashboardUtils';
import {
  Plus, Edit, Trash2, Eye, Phone, Mail, Send, UserX,
  MessageCircle, Shield, Search, Car
} from 'lucide-react';

const CustomersTabContent = ({
  customers, jobs, vehicles, searchQuery, setSearchQuery,
  can, user, onSendActivation, onResendActivation, onWhatsAppActivation,
  onAdminSetPassword, onImpersonate, onAddJob, onEdit, onDelete, onViewVehicleHistory
}) => {
  const filteredCustomers = searchQuery
    ? customers.filter(c => c.name?.toLowerCase().includes(searchQuery.toLowerCase()) || c.carPlate?.toLowerCase().includes(searchQuery.toLowerCase()) || c.phone?.includes(searchQuery) || c.email?.toLowerCase().includes(searchQuery.toLowerCase()))
    : customers;
  const isAdmin = user?.isAdmin || user?.role === 'admin';

  return (
    <>
      <div className="mb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Müşteri, plaka veya telefon ara..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            className="bg-gray-800 border-gray-700 text-white pl-10 h-9 text-sm" data-testid="customer-search" />
        </div>
      </div>
      <div className="space-y-2">
        {filteredCustomers.map(c => {
          const cJobs = jobs.filter(j => j.customerId === c.id);
          const portalStatus = c.portalStatus || 'draft';
          const hasEmail = !!c.email;
          const isActive = portalStatus === 'active';
          return (
            <Card key={c.id} className="bg-gray-800/60 border-gray-700/40" data-testid={`customer-card-${c.id}`}>
              <CardContent className="p-3">
                <div className="flex justify-between items-start">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-semibold text-sm">{c.name}</p>
                      {c.primaryBranchName && <BranchBadge branchId={c.primaryBranchId} branchName={c.primaryBranchName} small />}
                      {getPortalBadge(c)}
                      {c.lastActivationEmailAt && !isActive && (
                        <span className="text-gray-500 text-[10px]">
                          Son mail: {new Date(c.lastActivationEmailAt).toLocaleDateString('tr-TR')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5 flex-wrap">
                      {c.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</span>}
                      {c.email ? (
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</span>
                      ) : (
                        <span className="flex items-center gap-1 text-yellow-500"><UserX className="h-3 w-3" />Email yok</span>
                      )}
                      {c.carPlate && <span className="text-brand font-semibold">{c.carPlate}</span>}
                    </div>
                    {cJobs.length > 0 && (
                      <div className="flex gap-1 mt-1.5">
                        {cJobs.slice(0, 3).map(j => <React.Fragment key={j.id}>{getStatusBadge(j.status)}</React.Fragment>)}
                        {cJobs.length > 3 && <Badge className="bg-gray-600 text-xs">+{cJobs.length - 3}</Badge>}
                      </div>
                    )}
                    {(() => {
                      const cVehicles = vehicles.filter(v => v.customerId === c.id);
                      if (cVehicles.length === 0) return null;
                      return (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {cVehicles.map(v => (
                            <Badge key={v.id} className="bg-gray-700 text-brand/80 text-[10px] cursor-pointer hover:bg-gray-600"
                              onClick={() => onViewVehicleHistory(v.id)} data-testid={`cust-vehicle-${v.id}`}>
                              <Car className="h-2.5 w-2.5 mr-0.5" />{v.plate}
                            </Badge>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex gap-1 flex-shrink-0 flex-wrap justify-end">
                    {can('can_edit_customers') && hasEmail && !isActive && portalStatus === 'draft' && (
                      <Button size="sm" variant="outline"
                        className="border-green-700 text-green-400 text-xs h-7 hover:bg-green-900/30"
                        onClick={() => onSendActivation(c.id, c.name)}
                        data-testid={`send-activation-${c.id}`}>
                        <Send className="h-3 w-3 mr-1" />Davet
                      </Button>
                    )}
                    {can('can_edit_customers') && hasEmail && !isActive && portalStatus !== 'draft' && (
                      <Button size="sm" variant="outline"
                        className="border-blue-700 text-blue-400 text-xs h-7 hover:bg-blue-900/30"
                        onClick={() => onResendActivation(c.id, c.name)}
                        data-testid={`resend-activation-${c.id}`}>
                        <Send className="h-3 w-3 mr-1" />Tekrar Gonder
                      </Button>
                    )}
                    {can('can_edit_customers') && c.phone && !isActive && (
                      <Button size="sm" variant="outline"
                        className="border-green-600 text-green-400 text-xs h-7 hover:bg-green-900/30"
                        onClick={() => onWhatsAppActivation(c.id, c.name)}
                        data-testid={`whatsapp-activation-${c.id}`}>
                        <MessageCircle className="h-3 w-3 mr-1" />WA Link
                      </Button>
                    )}
                    {isAdmin && hasEmail && !isActive && (
                      <Button size="sm" variant="outline"
                        className="border-yellow-700 text-yellow-400 text-xs h-7 hover:bg-yellow-900/30"
                        onClick={() => onAdminSetPassword(c.id, c.name)}
                        data-testid={`admin-set-pwd-${c.id}`}>
                        <Shield className="h-3 w-3 mr-1" />Sifre Ata
                      </Button>
                    )}
                    {isAdmin && (
                      <Button size="sm" variant="outline"
                        className="border-purple-700 text-purple-400 text-xs h-7 hover:bg-purple-900/30"
                        onClick={() => onImpersonate(c.id, c.name)}
                        data-testid={`impersonate-${c.id}`}>
                        <Eye className="h-3 w-3 mr-1" />Goruntule
                      </Button>
                    )}
                    {can('can_create_jobs') && (
                      <Button size="sm" variant="outline" className="border-gray-600 text-gray-300 text-xs h-7"
                        onClick={() => onAddJob(c.id)}>
                        <Plus className="h-3 w-3" />
                      </Button>
                    )}
                    {can('can_edit_customers') && (
                      <Button size="sm" variant="outline" className="border-gray-600 text-gray-300 text-xs h-7"
                        onClick={() => onEdit(c)}>
                        <Edit className="h-3 w-3" />
                      </Button>
                    )}
                    {can('can_delete_customers') && (
                      <Button size="sm" variant="outline" className="border-red-800 text-red-400 text-xs h-7 hover:bg-red-900/30"
                        onClick={() => onDelete(c.id)} data-testid={`delete-customer-${c.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
};

export default CustomersTabContent;
